import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiUser, FiMail, FiLock, FiPhone, FiGift, FiArrowRight, FiArrowLeft, FiCheckCircle, FiZap, FiGlobe, FiEye, FiEyeOff } from 'react-icons/fi';
import styles from '../Login/Login.module.css';
import OtpVerify from '../../components/OtpVerify/OtpVerify';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { register } from '../../features/auth/authSlice';
import axiosClient from '../../api/axiosClient';
import { toast } from 'react-toastify';

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referredByCode, setReferredByCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useAppDispatch();
  const { status, error, pendingVerificationEmail } = useAppSelector((state) => state.auth);

  const [defaultRefCode, setDefaultRefCode] = useState('');

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setReferredByCode(ref);
    axiosClient.get('/settings/referral-code').then(({ data }) => setDefaultRefCode(data.defaultReferralCode)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    await dispatch(register({ fullName, email, phone, password, referredByCode }));
  };

  return (
    <div className={styles.wrap}>
      <Link to="/" className={styles.backLink}>
        <FiArrowLeft /> Back to website
      </Link>

      <div className={styles.brandPanel}>
        <div className={styles.gridOverlay} />
        <div className={`${styles.blob} ${styles.blob1}`} />
        <div className={`${styles.blob} ${styles.blob2}`} />
        <div className={`${styles.blob} ${styles.blob3}`} />

        <div className={styles.brandContent}>
          <div className={styles.brandEyebrow}>Block Cost Technologies</div>
          <h2 className={styles.brandTitle}>
            Join the <span>BCT</span> network of partners &amp; investors.
          </h2>
          <p className={styles.brandSub}>
            Create an account to submit meeting or investment requests and follow their progress
            end to end.
          </p>

          <div className={styles.chips}>
            <div className={styles.chip} style={{ animationDelay: '.1s' }}>
              <div className={styles.chipIcon}><FiZap /></div>
              <div className={styles.chipText}>
                <b>Instant setup</b>
                <span>Ready to submit requests in seconds</span>
              </div>
            </div>
            <div className={styles.chip} style={{ animationDelay: '.25s' }}>
              <div className={styles.chipIcon}><FiCheckCircle /></div>
              <div className={styles.chipText}>
                <b>Auto-linked history</b>
                <span>Past requests attach to your email</span>
              </div>
            </div>
            <div className={styles.chip} style={{ animationDelay: '.4s' }}>
              <div className={styles.chipIcon}><FiGlobe /></div>
              <div className={styles.chipText}>
                <b>Global access</b>
                <span>Manage requests from anywhere</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formPanel}>
        {pendingVerificationEmail ? (
          <OtpVerify email={pendingVerificationEmail} />
        ) : (
        <div className={styles.card}>
          <div className={styles.eyebrow}>BCT Platform</div>
          <h1 className={styles.title}>Create your account</h1>
          <p className={styles.subtitle}>Track your meeting &amp; investment requests in one place.</p>

          {error && (
            <div className={styles.error}>
              {error}
              {error.toLowerCase().includes('already exists') && (
                <> <Link to="/login" style={{ textDecoration: 'underline', fontWeight: 700 }}>Login here →</Link></>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="on">
            <div className={styles.field}>
              <label className={styles.label}>Full name</label>
              <div className={styles.inputWrap}>
                <FiUser className={styles.inputIcon} />
                <input className={styles.input} name="name" autoComplete="name" placeholder="Your name" value={fullName}
                  onChange={(e) => setFullName(e.target.value)} required />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <div className={styles.inputWrap}>
                <FiMail className={styles.inputIcon} />
                <input type="email" className={styles.input} name="email" autoComplete="email" placeholder="support@blockcostlabs.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Phone</label>
              <div className={styles.inputWrap}>
                <FiPhone className={styles.inputIcon} />
                <input type="tel" className={styles.input} name="tel" autoComplete="tel" placeholder="+91 XXXXX XXXXX" value={phone}
                  onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Referral code <span style={{ color: 'var(--danger, #e2534a)' }}>*</span></label>
              <div className={styles.inputWrap}>
                <FiGift className={styles.inputIcon} />
                <input className={styles.input} name="referral-code" autoComplete="off" placeholder="Enter the referral code you were given" value={referredByCode} required
                  onChange={(e) => setReferredByCode(e.target.value.toUpperCase())} />
              </div>
              {defaultRefCode && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted)' }}>
                  Don't have one?{' '}
                  <button
                    type="button"
                    onClick={() => setReferredByCode(defaultRefCode)}
                    style={{ color: 'var(--copper-light)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    Use BlockCost's code ({defaultRefCode})
                  </button>
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Password</label>
              <div className={styles.inputWrap}>
                <FiLock className={styles.inputIcon} />
                <input type={showPassword ? 'text' : 'password'} className={styles.input} name="new-password" autoComplete="new-password" placeholder="Min 6 characters" value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                <button type="button" className={styles.eyeToggle} onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Confirm Password</label>
              <div className={styles.inputWrap}>
                <FiLock className={styles.inputIcon} />
                <input type={showConfirmPassword ? 'text' : 'password'} className={styles.input} name="confirm-password" autoComplete="new-password" placeholder="Re-enter your password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
                <button type="button" className={styles.eyeToggle} onClick={() => setShowConfirmPassword((v) => !v)} tabIndex={-1}>
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={status === 'loading'}>
              {status === 'loading' ? <span className={styles.spinner} /> : <>Create account <FiArrowRight /></>}
            </button>
          </form>

          <div className={styles.footer}>
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Register;
